import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Heart, FileText, Video, Calculator, TrendingUp, DollarSign, Info, Bell } from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';
export function TaxForexPage() {
  const [propertyValue, setPropertyValue] = useState('5000000');
  const [selectedState, setSelectedState] = useState('Maharashtra');
  const [forexRates, setForexRates] = useState({
    USD: 83.42,
    AED: 22.71,
    GBP: 101.85,
    EUR: 88.12
  });
  const [isLoadingForex, setIsLoadingForex] = useState(false);
  // State-specific tax rates
  const stateRates: Record<string, {
    stampDuty: number;
    registration: number;
  }> = {
    Maharashtra: {
      stampDuty: 0.06,
      registration: 0.01
    },
    Karnataka: {
      stampDuty: 0.05,
      registration: 0.01
    },
    Telangana: {
      stampDuty: 0.04,
      registration: 0.01
    },
    Gujarat: {
      stampDuty: 0.049,
      registration: 0.01
    },
    'Tamil Nadu': {
      stampDuty: 0.07,
      registration: 0.01
    },
    Delhi: {
      stampDuty: 0.06,
      registration: 0.01
    }
  };
  // Calculate taxes based on property value and state
  const calculateTaxes = () => {
    const value = parseFloat(propertyValue.replace(/,/g, ''));
    const rates = stateRates[selectedState];
    const stampDuty = value * rates.stampDuty;
    const registration = Math.min(value * rates.registration, 30000); // Capped at 30k
    const tds = value > 5000000 ? value * 0.01 : 0; // 1% TDS if value > 50L
    const total = value + stampDuty + registration + tds;
    return {
      stampDuty: stampDuty.toLocaleString('en-IN'),
      stampDutyPercent: (rates.stampDuty * 100).toFixed(1),
      registration: registration.toLocaleString('en-IN'),
      tds: tds.toLocaleString('en-IN'),
      total: total.toLocaleString('en-IN')
    };
  };
  const taxes = calculateTaxes();
  // Fetch live forex rates
  useEffect(() => {
    const fetchForexRates = async () => {
      setIsLoadingForex(true);
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.INR) {
          const usdToInr = data.rates.INR;
          const aedToInr = usdToInr / 3.67;
          const gbpToInr = data.rates.GBP ? usdToInr / data.rates.GBP : 101.85;
          const eurToInr = data.rates.EUR ? usdToInr / data.rates.EUR : 88.12;
          setForexRates({
            USD: parseFloat(usdToInr.toFixed(2)),
            AED: parseFloat(aedToInr.toFixed(2)),
            GBP: parseFloat(gbpToInr.toFixed(2)),
            EUR: parseFloat(eurToInr.toFixed(2))
          });
        }
      } catch (error) {
        console.error('Failed to fetch forex rates:', error);
      } finally {
        setIsLoadingForex(false);
      }
    };
    fetchForexRates();
    const interval = setInterval(fetchForexRates, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);
  const navItems = [{
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard/nri-buyer'
  }, {
    label: 'Shortlisted',
    icon: Heart,
    path: '/buyer/shortlisted'
  }, {
    label: 'POA & Legal',
    icon: FileText,
    path: '/nri/poa-legal'
  }, {
    label: 'Virtual Tours',
    icon: Video,
    path: '/nri/virtual-tours'
  }, {
    label: 'Tax & Forex',
    icon: Calculator,
    path: '/nri/tax-forex'
  }];
  return <DashboardLayout title="NRI Services" personaLabel="NRI MODE" personaColor="bg-purple-100 text-purple-800 border-purple-200" navItems={navItems} userInitials="NR" data-id="element-4919">
      <h1 className="text-2xl font-bold mb-2" data-id="element-4920">Tax & Forex Tools</h1>
      <p className="text-gray-500 mb-8 font-mono text-sm" data-id="element-4921">
        Calculate costs and track exchange rates.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4922">
        {/* Forex Tracker */}
        <div className="bg-white border border-gray-300 p-6" data-id="element-4923">
          <div className="flex justify-between items-center mb-6" data-id="element-4924">
            <h2 className="font-bold text-lg flex items-center gap-2" data-id="element-4925">
              <TrendingUp className="w-5 h-5" data-id="element-4926" /> Live Exchange Rates
            </h2>
            <span className="text-xs font-mono text-green-600 animate-pulse" data-id="element-4927">
              ● Live
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-4928">
            <div className="p-4 bg-gray-50 border border-gray-200 text-center" data-id="element-4929">
              <div className="text-sm text-gray-500 mb-1" data-id="element-4930">USD to INR</div>
              <div className="text-2xl font-bold text-[#1f2933]" data-id="element-4931">
                {isLoadingForex ? '...' : `₹ ${forexRates.USD}`}
              </div>
              <div className="text-xs text-green-600 font-mono mt-1" data-id="element-4932">
                +0.12%
              </div>
            </div>
            <div className="p-4 bg-gray-50 border border-gray-200 text-center" data-id="element-4933">
              <div className="text-sm text-gray-500 mb-1" data-id="element-4934">AED to INR</div>
              <div className="text-2xl font-bold text-[#1f2933]" data-id="element-4935">
                {isLoadingForex ? '...' : `₹ ${forexRates.AED}`}
              </div>
              <div className="text-xs text-red-600 font-mono mt-1" data-id="element-4936">-0.05%</div>
            </div>
            <div className="p-4 bg-gray-50 border border-gray-200 text-center" data-id="element-4937">
              <div className="text-sm text-gray-500 mb-1" data-id="element-4938">GBP to INR</div>
              <div className="text-2xl font-bold text-[#1f2933]" data-id="element-4939">
                {isLoadingForex ? '...' : `₹ ${forexRates.GBP}`}
              </div>
              <div className="text-xs text-green-600 font-mono mt-1" data-id="element-4940">
                +0.24%
              </div>
            </div>
            <div className="p-4 bg-gray-50 border border-gray-200 text-center" data-id="element-4941">
              <div className="text-sm text-gray-500 mb-1" data-id="element-4942">EUR to INR</div>
              <div className="text-2xl font-bold text-[#1f2933]" data-id="element-4943">
                {isLoadingForex ? '...' : `₹ ${forexRates.EUR}`}
              </div>
              <div className="text-xs text-gray-500 font-mono mt-1" data-id="element-4944">0.00%</div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-100 rounded-sm" data-id="element-4945">
            <h3 className="font-bold text-sm text-blue-900 mb-2 flex items-center gap-2" data-id="element-4946">
              <Bell className="w-4 h-4" data-id="element-4947" /> Rate Alert
            </h3>
            <p className="text-xs text-blue-800 mb-3" data-id="element-4948">
              Get notified when USD hits ₹83.00
            </p>
            <button className="text-xs font-bold text-blue-700 underline" data-id="element-4949">
              Manage Alerts
            </button>
          </div>
        </div>

        {/* Tax Calculator */}
        <div className="bg-white border border-gray-300 p-6" data-id="element-4950">
          <h2 className="font-bold text-lg mb-6 flex items-center gap-2" data-id="element-4951">
            <Calculator className="w-5 h-5" data-id="element-4952" /> Property Tax Estimator
          </h2>

          <div className="space-y-4 mb-6" data-id="element-4953">
            <div data-id="element-4954">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1" data-id="element-4955">
                Property Value (INR)
              </label>
              <input type="text" value={propertyValue} onChange={e => setPropertyValue(e.target.value.replace(/[^0-9]/g, ''))} className="w-full p-2 border border-gray-300 bg-gray-50 font-mono" data-id="element-4956" />
            </div>
            <div data-id="element-4957">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1" data-id="element-4958">
                State
              </label>
              <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="w-full p-2 border border-gray-300 bg-gray-50" data-id="element-4959">
                {Object.keys(stateRates).map(state => <option key={state} value={state} data-id="element-4960">
                    {state}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="bg-gray-50 border border-dashed border-gray-300 p-4 space-y-2" data-id="element-4961">
            <div className="flex justify-between text-sm" data-id="element-4962">
              <span className="text-gray-600" data-id="element-4963">
                Stamp Duty ({taxes.stampDutyPercent}%)
              </span>
              <span className="font-mono font-bold" data-id="element-4964">₹ {taxes.stampDuty}</span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-4965">
              <span className="text-gray-600" data-id="element-4966">Registration Fee (1%)</span>
              <span className="font-mono font-bold" data-id="element-4967">
                ₹ {taxes.registration}
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-4968">
              <span className="text-gray-600" data-id="element-4969">TDS (1% if &gt; 50L)</span>
              <span className="font-mono font-bold" data-id="element-4970">₹ {taxes.tds}</span>
            </div>
            <div className="pt-2 mt-2 border-t border-gray-300 flex justify-between font-bold" data-id="element-4971">
              <span data-id="element-4972">Total Estimated Cost</span>
              <span data-id="element-4973">₹ {taxes.total}</span>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500" data-id="element-4974">
            <Info className="w-4 h-4 mt-0.5 shrink-0" data-id="element-4975" />
            <p data-id="element-4976">
              Estimates are indicative. Actual charges may vary based on
              municipal corporation rules and property type.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>;
}